import type { ColumnDef } from '@tanstack/react-table';
import { Plus, Warehouse } from 'lucide-react';
import { useDeferredValue, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { DataTable } from '@/components/ui/data-table';
import { EmptyState } from '@/components/ui/empty-state';
import { Input } from '@/components/ui/input';
import { PartPicker } from '@/components/ui/part-picker';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { VesselSelect } from '@/components/ui/vessel-select';
import type { Part } from '@/features/parts/types';
import { useParts } from '@/features/parts/queries';
import { useStockLots } from '@/features/stock-lots/queries';
import {
  isLowStock,
  isOverstock,
  resolveMaxStock,
  resolveMinStock,
  type StockLot,
} from '@/features/stock-lots/types';
import { useVessels } from '@/features/vessels/queries';
import { vesselDisplayName, type Vessel } from '@/features/vessels/types';
import { ApiError } from '@/lib/api';
import { matchesQuery } from '@/lib/list-search';
import { cn } from '@/lib/utils';

/** Loading skeleton — 5 rows × 7 columns matching the table shape. */
function ListTableSkeleton() {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          {Array.from({ length: 7 }).map((_, i) => (
            <TableHead key={i}>
              <Skeleton className="h-3 w-20" />
            </TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {Array.from({ length: 5 }).map((_, r) => (
          <TableRow key={r}>
            {Array.from({ length: 7 }).map((_, c) => (
              <TableCell key={c}>
                <Skeleton className="h-4 w-full" />
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

/**
 * Quantity badge — colour-coded by stock state:
 *   low (qty < effectiveMin)        → red
 *   over (qty > effectiveMax)       → amber
 *   nominal                         → emerald
 *
 * The badge text shows the raw quantity. Hover-style tooltip would
 * show "X / min Y / max Z" but we keep it minimal here; the detail
 * page surfaces all three values in dedicated fields.
 */
function QuantityBadge({
  quantity,
  isLow,
  isOver,
}: {
  quantity: number;
  isLow: boolean;
  isOver: boolean;
}) {
  const tone = isLow
    ? 'bg-red-100 text-red-700'
    : isOver
      ? 'bg-amber-100 text-amber-800'
      : 'bg-emerald-100 text-emerald-700';
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-md px-2 py-0.5 text-xs font-semibold tabular-nums',
        tone,
      )}
    >
      {quantity}
    </span>
  );
}

/**
 * Format a "last movement at" timestamp as a short relative-ish label
 * (e.g. "today", "3d ago", "—" if null). Stays English-agnostic by
 * returning numeric variants and letting i18n handle the suffix.
 */
function formatLastMovement(
  iso: string | null,
  t: (k: string, opts?: Record<string, unknown>) => string,
): string {
  if (!iso) return '—';
  const date = new Date(iso);
  const diffMs = Date.now() - date.getTime();
  const diffDays = Math.floor(diffMs / 86_400_000);
  if (diffDays <= 0) return t('stockLot.lastMovement.today');
  if (diffDays === 1) return t('stockLot.lastMovement.yesterday');
  if (diffDays < 30)
    return t('stockLot.lastMovement.daysAgo', { count: diffDays });
  return date.toLocaleDateString();
}

export function StockLotsListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const vesselId = searchParams.get('vesselId') ?? undefined;
  const partId = searchParams.get('partId') ?? undefined;
  const lowStockOnly = searchParams.get('lowStock') === 'true';

  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
    isFetching,
  } = useStockLots({
    vesselId,
    partId,
    lowStockOnly: lowStockOnly || undefined,
  });

  const [search, setSearch] = useState('');
  const deferredSearch = useDeferredValue(search);

  const { data: parts } = useParts();
  const { data: vessels } = useVessels();

  // Resolve partId → Part for column rendering. Memoised Map for O(1)
  // lookup so rows render in O(n).
  const partsMap = useMemo(() => {
    const m = new Map<string, Part>();
    (parts ?? []).forEach((p) => m.set(p.id, p));
    return m;
  }, [parts]);

  const vesselsMap = useMemo(() => {
    const m = new Map<string, Vessel>();
    (vessels ?? []).forEach((v) => m.set(v.id, v));
    return m;
  }, [vessels]);

  const filtered = useMemo(() => {
    const list = data ?? [];
    return list.filter((lot) => {
      const part = partsMap.get(lot.partId);
      return matchesQuery(lot, deferredSearch, [
        () => part?.code,
        () => part?.name,
        () => part?.manufacturer,
        () => lot.location,
        () => vesselsMap.get(lot.vesselId)?.name,
      ]);
    });
  }, [data, deferredSearch, partsMap, vesselsMap]);

  const columns = useMemo<ColumnDef<StockLot>[]>(
    () => [
      {
        accessorKey: 'partId',
        header: () => t('stockLot.columns.part'),
        enableSorting: false,
        cell: ({ row }) => {
          const part = partsMap.get(row.original.partId);
          return (
            <Link
              to={`/stock-lots/${row.original.id}`}
              onClick={(e) => e.stopPropagation()}
              className="text-primary hover:underline"
            >
              {part ? (
                <>
                  <span className="font-mono text-sm">{part.code}</span>
                  <span className="ml-2">{part.name}</span>
                </>
              ) : (
                <span className="font-mono text-sm">{row.original.partId}</span>
              )}
            </Link>
          );
        },
      },
      {
        accessorKey: 'vesselId',
        header: () => t('stockLot.columns.vessel'),
        enableSorting: false,
        cell: ({ row }) => {
          const v = vesselsMap.get(row.original.vesselId);
          return v ? vesselDisplayName(v) : '—';
        },
      },
      {
        accessorKey: 'location',
        header: () => t('stockLot.columns.location'),
      },
      {
        accessorKey: 'quantityOnHand',
        header: () => t('stockLot.columns.quantity'),
        cell: ({ row }) => {
          const part = partsMap.get(row.original.partId);
          return (
            <QuantityBadge
              quantity={row.original.quantityOnHand}
              isLow={isLowStock(row.original, part?.minStockGlobal)}
              isOver={isOverstock(row.original, part?.maxStockGlobal)}
            />
          );
        },
      },
      {
        id: 'minStock',
        header: () => t('stockLot.columns.minStock'),
        enableSorting: false,
        cell: ({ row }) => {
          const part = partsMap.get(row.original.partId);
          const eff = resolveMinStock(row.original, part?.minStockGlobal);
          const overridden = row.original.minStockOverride !== null;
          return (
            <span className="tabular-nums">
              {eff}
              {overridden && (
                <span className="ml-1 text-xs text-muted-foreground">
                  ({t('stockLot.detail.override')})
                </span>
              )}
            </span>
          );
        },
      },
      {
        id: 'maxStock',
        header: () => t('stockLot.columns.maxStock'),
        enableSorting: false,
        cell: ({ row }) => {
          const part = partsMap.get(row.original.partId);
          const eff = resolveMaxStock(row.original, part?.maxStockGlobal);
          if (!Number.isFinite(eff)) return '—';
          const overridden = row.original.maxStockOverride !== null;
          return (
            <span className="tabular-nums">
              {eff}
              {overridden && (
                <span className="ml-1 text-xs text-muted-foreground">
                  ({t('stockLot.detail.override')})
                </span>
              )}
            </span>
          );
        },
      },
      {
        accessorKey: 'lastMovementAt',
        header: () => t('stockLot.columns.lastMovement'),
        cell: ({ getValue }) =>
          formatLastMovement(getValue<string | null>(), t),
      },
    ],
    [t, partsMap, vesselsMap],
  );

  const errorMessage = (() => {
    if (!isError) return null;
    if (error instanceof ApiError) {
      return t(error.key, {
        defaultValue: t('stockLot.list.loadError'),
        ...(error.params ?? {}),
      });
    }
    return t('stockLot.list.loadError');
  })();

  const setFilter = (key: string, next: string | null) => {
    const params = new URLSearchParams(searchParams);
    if (next === null) params.delete(key);
    else params.set(key, next);
    setSearchParams(params, { replace: true });
  };

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold">{t('stockLot.list.title')}</h1>
        <Button asChild>
          <Link to="/stock-lots/new">
            <Plus aria-hidden />
            {t('stockLot.list.newButton')}
          </Link>
        </Button>
      </header>

      {/* Filters row */}
      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-muted-foreground">
            {t('common.search')}
          </span>
          <Input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('stockLot.list.searchPlaceholder')}
            className="h-9 w-64"
            data-testid="stock-lots-search"
          />
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-muted-foreground">
            {t('stockLot.list.filterByVessel')}
          </span>
          <VesselSelect
            value={vesselId ?? null}
            onChange={(v) => setFilter('vesselId', v)}
            allowEmpty
            emptyLabel={t('stockLot.list.allVessels')}
            className="h-9 w-56"
          />
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-muted-foreground">
            {t('stockLot.list.filterByPart')}
          </span>
          <PartPicker
            value={partId ?? null}
            onChange={(v) => setFilter('partId', v)}
            allowEmpty
            emptyLabel={t('stockLot.list.allParts')}
            className="h-9 w-72"
          />
        </label>
        <label className="flex items-center gap-2 self-end pb-2 text-sm">
          <input
            type="checkbox"
            checked={lowStockOnly}
            onChange={(e) =>
              setFilter('lowStock', e.target.checked ? 'true' : null)
            }
          />
          {t('stockLot.list.lowStockOnly')}
        </label>
      </div>

      {errorMessage && (
        <Alert variant="destructive">
          <AlertTitle>{t('stockLot.list.loadError')}</AlertTitle>
          <AlertDescription className="space-y-2">
            <p>{errorMessage}</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetch()}
              disabled={isFetching}
            >
              {t('common.retry')}
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {!isError && (
        <DataTable
          columns={columns}
          data={filtered}
          isLoading={isLoading}
          loadingState={<ListTableSkeleton />}
          emptyState={
            <EmptyState
              icon={Warehouse}
              title={t('stockLot.list.empty')}
              description={t('stockLot.list.emptyHint')}
              action={
                <Button asChild size="sm">
                  <Link to="/stock-lots/new">
                    <Plus aria-hidden />
                    {t('stockLot.list.newButton')}
                  </Link>
                </Button>
              }
            />
          }
          onRowClick={(lot) => navigate(`/stock-lots/${lot.id}`)}
        />
      )}
    </div>
  );
}
